import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import ViverseButton from '@/components/ViverseButton'
import ViverseCheckbox from '@/components/ViverseCheckbox'
import ViverseInput from '@/components/ViverseInput'
import ViverseTextarea from '@/components/ViverseTextarea'
import { MAX_TITLE_LENGTH, WorldSettingsFormValues } from '@/hooks/useWorldSettingsFormSchema'
import { cn } from '@/lib/utils'
import { ImageAsset } from '@/types/world'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { ChangeEvent, FC, JSX, RefObject } from 'react'
import { useFormContext } from 'react-hook-form'

const ACCEPTED_FILE_TYPES_IMAGE = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'] as const

export type WorldSettingBasicTabProps = {
  genreOptions: Array<{ label: string; value: string }>
  deviceOptions: Array<{ label: string; value: string }>
  handleGenreValueChange: (value: string) => void
  handleDeviceValueChange: (value: string) => void
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  inputRef: RefObject<HTMLInputElement | null>
  uploadError?: Error
  defaultThumbnail?: ImageAsset
  roomDefaultImage: string
  isPublicPolicy: boolean
  renderRequiredDot: () => JSX.Element
  selectedGenres: string[]
  selectedDevices: string[]
  thumbnailPreview?: string
}

const WorldSettingBasicTab: FC<WorldSettingBasicTabProps> = ({
  genreOptions,
  deviceOptions,
  handleGenreValueChange,
  handleDeviceValueChange,
  selectedGenres,
  selectedDevices,
  handleInputChange,
  inputRef,
  uploadError,
  defaultThumbnail,
  roomDefaultImage,
  isPublicPolicy,
  renderRequiredDot,
  thumbnailPreview,
}) => {
  const t = useTranslations()
  const form = useFormContext<WorldSettingsFormValues>()
  const formErrors = form.formState.errors

  const handleClickUploadThumbnailButton = () => {
    inputRef.current?.click()
  }

  return (
    <div className="flex w-full flex-1 flex-col">
      <p className="md:subheader-1 subheader-2 mb-2 md:mb-4">
        {t('profile-page.my-worlds-card-settings-basic-info')}
      </p>
      <div className="flex w-full flex-col gap-4 md:gap-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem className="flex w-full flex-col">
              <FormLabel className="subheader-3 text-vive-primary-20">
                {t('profile-page.my-worlds-card-settings-title')}
                {isPublicPolicy && formErrors.title && renderRequiredDot()}
              </FormLabel>
              <FormControl>
                <ViverseInput
                  placeholder={t('profile-page.my-worlds-card-settings-title-caption')}
                  maxLength={MAX_TITLE_LENGTH}
                  className="bg-transparent"
                  containerClassName="bg-vive-primary-70/50 border-none"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e)
                    form.trigger('title')
                  }}
                />
              </FormControl>
              <FormMessage className="text-[12px]" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="flex w-full flex-1 flex-col">
              <FormLabel className="subheader-3 text-vive-primary-20">
                {t('profile-page.my-worlds-card-settings-description')}
              </FormLabel>
              <FormControl>
                <ViverseTextarea
                  placeholder={t('profile-page.my-worlds-card-settings-description-caption')}
                  maxLength={500}
                  rows={8}
                  className="bg-transparent"
                  containerClassName="bg-vive-primary-70/50 border-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div id="upload-thumbnail-panel" className="text-center">
          <div className="mb-2">
            <div className="text-left">
              <FormLabel className="subheader-3 text-vive-primary-20">
                {t('profile-page.my-worlds-card-settings-thumbnail-select')}
              </FormLabel>
              {uploadError && <div className="caption text-vive-error">{uploadError.message}</div>}
            </div>
          </div>
          <div className="flex flex-row gap-6">
            <div className="relative min-h-[116px] w-[200px] overflow-hidden rounded-lg md:min-h-[178px] md:w-[306px]">
              <Image
                src={thumbnailPreview || defaultThumbnail?.url || roomDefaultImage}
                alt="uploaded image of room"
                style={{ objectFit: 'cover' }}
                fill
              />
            </div>

            <div className="flex flex-col">
              <div className="text-left">
                <p className="caption text-vive-primary-30">
                  {t('profile-page.my-worlds-card-settings-thumbnail-maximum')}: 2MB
                </p>
                <p className="caption text-vive-primary-30">
                  {t('profile-page.my-worlds-card-settings-thumbnail-ratio')}: 16:9
                </p>
                <p className="caption text-vive-primary-30">
                  {t('profile-page.my-worlds-card-settings-thumbnail-format')}: JPG, PNG, GIF
                </p>
              </div>
              <ViverseButton
                className="mt-4"
                onClick={handleClickUploadThumbnailButton}
                variant="secondary"
                size="small2"
              >
                {t('profile-page.my-worlds-card-settings-thumbnail')}
              </ViverseButton>
            </div>
          </div>
        </div>

        <FormField
          control={form.control}
          name="genre"
          render={() => (
            <FormItem className="flex w-full flex-col gap-2">
              <FormLabel>
                <div className="subheader-3 text-vive-primary-20">
                  {t('profile-page.my-worlds-card-settings-genre')}
                  {isPublicPolicy && formErrors.genre && renderRequiredDot()}
                </div>
                <p className="caption text-vive-primary-30">
                  {t('profile-page.my-worlds-card-settings-genre-caption')}
                </p>
              </FormLabel>
              <FormControl>
                <div className="flex flex-wrap gap-x-2 gap-y-4">
                  {genreOptions.map((genre, i) => {
                    return (
                      <div
                        key={i}
                        onClick={() => {
                          handleGenreValueChange(genre.value)
                          form.trigger('genre')
                        }}
                        className={cn(
                          'bg-vive-primary-70/50 text-vive-primary-20 hover:bg-vive-primary-40/50 body-2 cursor-pointer rounded-[7px] px-3 py-[6px]',
                          selectedGenres.includes(genre.value) && 'text-vive-brand-50',
                        )}
                      >
                        {genre.label}
                      </div>
                    )
                  })}
                </div>
              </FormControl>
              <FormMessage className="text-[12px]" />
            </FormItem>
          )}
        />
      </div>
      <p className="subheader-2 mt-8 mb-4">{t('profile-page.my-worlds-card-settings-devices')}</p>
      <div className="flex w-full flex-col gap-4 md:gap-6">
        <FormField
          control={form.control}
          name="preferredDevices"
          render={() => (
            <FormItem>
              <FormLabel className="flex flex-col gap-1">
                <FormLabel className="subheader-3 text-vive-primary-20">
                  {t('spaces-create-modal.device-filter')}
                </FormLabel>
                <p className="caption text-vive-primary-30">
                  {t('spaces-create-modal.device-filter-desc')}
                </p>
              </FormLabel>
              <FormControl>
                <div>
                  {deviceOptions.map((device, i) => {
                    return (
                      <ViverseCheckbox
                        key={i}
                        checked={selectedDevices.includes(device.value)}
                        id="embed-checkbox"
                        onClick={() => handleDeviceValueChange(device.value)}
                      >
                        {device.label}
                      </ViverseCheckbox>
                    )
                  })}
                </div>
              </FormControl>
              <FormMessage className="caption" />
            </FormItem>
          )}
        />
      </div>
      <input
        type="file"
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={handleInputChange}
        accept={ACCEPTED_FILE_TYPES_IMAGE.join(',')}
      />
    </div>
  )
}

export default WorldSettingBasicTab
